/**
 * iPad: Field Lube Report (Rapid Lube). Items and lubricants come from the profile lists in
 * the job payload, so it works offline. "Lube Report Done" saves and marks it complete; the
 * server checks it is really complete (D21). Every item can be Check, Change or N/A; 15W40,
 * AW46 and HYTRANS (profile lube.supplyRequired) need Bulk or Buckets when gallons are entered.
 */
import { useState } from 'react';
import { OTHER_LUBRICANT } from '@wo/shared';
import { enqueueItem } from './queue';
import type { FormLists, MechForms, MechJob } from './types';
import { Big, BigField, Label, NoteBox, Sheet, bigInput } from './ui';

type SetForms = (fn: (f: MechForms) => MechForms) => void;

export function LubeSheet({ job, lists, userId, setForms, onClose }: { job: MechJob; lists: FormLists; userId: number; setForms: SetForms; onClose: () => void }) {
  const l = job.forms.lube;
  const [h, setH] = useState({
    customerEquipmentNo: l?.header.customerEquipmentNo ?? job.machine.equipmentNo ?? '',
    equipHours: l?.header.hours ?? '',
    typeOfService: l?.header.typeOfService ?? '',
    nextService: l?.header.nextService ?? '',
  });
  const [remarks, setRemarks] = useState(l?.remarks ?? '');
  const row = (item: string) => l?.items.find((i) => i.item === item);
  const [items, setItems] = useState(lists.lubeItems.map((item) => ({ item, checked: row(item)?.checked ?? false, changed: row(item)?.changed ?? false, na: row(item)?.na ?? false })));
  const [lubes, setLubes] = useState([
    ...lists.lubricants.map((lubricant) => {
      const x = l?.lubricants.find((y) => y.lubricant === lubricant);
      return { lubricant, otherName: '', gallons: x?.gallons ?? '', supply: x?.supply ?? '' };
    }),
    ...(l?.lubricants.filter((x) => x.lubricant === OTHER_LUBRICANT).map((x) => ({ ...x, gallons: x.gallons ?? '', supply: x.supply ?? '' })) ?? []),
  ]);
  const options = lists.supplyOptions ?? [];
  const required = lists.lubeSupplyRequired ?? [];
  const needsSupply = lubes.filter((x) => required.includes(x.lubricant) && x.gallons !== '' && Number(x.gallons) > 0 && !x.supply);
  const canEdit = job.status === 'ASSIGNED' || job.status === 'IN_PROGRESS' || job.status === 'ON_HOLD';
  const hoursOk = h.equipHours === '' || /^\d{1,9}(\.\d)?$/.test(h.equipHours);
  const gallonsOk = lubes.every((x) => x.gallons === '' || /^\d{1,8}(\.\d{1,2})?$/.test(x.gallons)) && needsSupply.length === 0;
  const complete = h.equipHours !== '' && h.typeOfService.trim() !== '' && items.some((i) => i.checked || i.changed);

  const save = async (done: boolean) => {
    // Send filled-in rows, and rows that had gallons before (cleared = 0; rows are never deleted).
    const had = (x: { lubricant: string; otherName: string }) => l?.lubricants.some((y) => y.lubricant === x.lubricant && y.otherName === x.otherName.trim() && y.gallons);
    const lubricants = lubes.filter((x) => (x.gallons !== '' || had(x)) && (x.lubricant !== OTHER_LUBRICANT || x.otherName.trim())).map((x) => ({ lubricant: x.lubricant, otherName: x.otherName.trim(), gallons: x.gallons, supply: x.supply }));
    await enqueueItem(userId, `Lube report #${job.number}`, { type: 'lube_save', workOrderId: job.id, ...h, remarks, items, lubricants });
    if (done) await enqueueItem(userId, `Lube report done #${job.number}`, { type: 'lube_submit', workOrderId: job.id });
    setForms((f) => ({
      ...f,
      lube: {
        header: { ...(f.lube?.header ?? { customerName: job.customer.name, make: job.machine.make, model: job.machine.model, serialNumber: job.machine.serialNumber, machineLocation: job.machineLocation }), customerEquipmentNo: h.customerEquipmentNo, hours: h.equipHours || null, typeOfService: h.typeOfService || null, nextService: h.nextService || null },
        items,
        lubricants: lubes,
        remarks,
        submittedAt: done ? new Date().toISOString() : (f.lube?.submittedAt ?? null),
        missing: done ? [] : (f.lube?.missing ?? []),
      },
    }));
    onClose();
  };

  // N/A is neither Check nor Change: choosing one clears the other.
  const toggle = (i: number, k: 'checked' | 'changed' | 'na') =>
    setItems(items.map((x, j) => (j !== i ? x : k === 'na' ? { ...x, na: !x.na, ...(!x.na ? { checked: false, changed: false } : {}) } : { ...x, [k]: !x[k], ...(!x[k] ? { na: false } : {}) })));

  return (
    <Sheet title={`Field Lube Report${lists.lubeBrand ? ` (${lists.lubeBrand})` : ''} — #${job.number}`} onClose={onClose}>
      {l?.submittedAt && <p className="mb-2 rounded-lg bg-green-100 p-3 text-lg text-green-900">Marked done. You can still fix it until the job goes for review.</p>}
      <p className="text-lg">{job.customer.name} · {job.machine.make} {job.machine.model} · S/N {job.machine.serialNumber}</p>
      <div className="grid grid-cols-2 gap-x-3">
        <BigField label="Customer equipment no." value={h.customerEquipmentNo} onChange={(e) => setH({ ...h, customerEquipmentNo: e.target.value })} disabled={!canEdit} />
        <BigField label="Hours" inputMode="decimal" value={h.equipHours} onChange={(e) => setH({ ...h, equipHours: e.target.value.replace(/[^\d.]/g, '') })} disabled={!canEdit} />
        <BigField label="Type of service" value={h.typeOfService} onChange={(e) => setH({ ...h, typeOfService: e.target.value })} disabled={!canEdit} placeholder="e.g. 500 hour" />
        <BigField label="Next service" value={h.nextService} onChange={(e) => setH({ ...h, nextService: e.target.value })} disabled={!canEdit} />
      </div>

      <Label>Check / Change</Label>
      <ul className="divide-y rounded-xl border-2 border-slate-200">
        {items.map((it, i) => (
          <li key={it.item} className="flex items-center gap-2 p-2">
            <span className="flex-1 text-lg">{it.item}</span>
            <Big tone={it.checked ? 'go' : 'light'} className="min-h-[56px] min-w-[100px] border-2 border-slate-300 px-2" disabled={!canEdit} onClick={() => toggle(i, 'checked')}>{it.checked ? '✓ Check' : 'Check'}</Big>
            <Big tone={it.changed ? 'go' : 'light'} className="min-h-[56px] min-w-[110px] border-2 border-slate-300 px-2" disabled={!canEdit} onClick={() => toggle(i, 'changed')}>{it.changed ? '✓ Change' : 'Change'}</Big>
            <Big tone={it.na ? 'neutral' : 'light'} className="min-h-[56px] min-w-[80px] border-2 border-slate-300 px-2" disabled={!canEdit} onClick={() => toggle(i, 'na')}>{it.na ? '✓ N/A' : 'N/A'}</Big>
          </li>
        ))}
      </ul>

      <Label>Lubricants — gallons</Label>
      <div className="grid grid-cols-2 gap-2">
        {lubes.map((x, i) => (
          <div key={`${x.lubricant}:${i}`} className="flex flex-wrap items-center gap-2">
            {x.lubricant === OTHER_LUBRICANT ? (
              <input className={`${bigInput} flex-1`} placeholder="Other (name)" value={x.otherName} disabled={!canEdit} onChange={(e) => setLubes(lubes.map((y, j) => (j === i ? { ...y, otherName: e.target.value } : y)))} />
            ) : (
              <span className="flex-1 text-lg font-semibold">{x.lubricant}</span>
            )}
            <input className={`${bigInput} w-28`} inputMode="decimal" aria-label={`${x.lubricant} gallons`} value={x.gallons} disabled={!canEdit} onChange={(e) => setLubes(lubes.map((y, j) => (j === i ? { ...y, gallons: e.target.value.replace(/[^\d.]/g, '') } : y)))} />
            {(required.includes(x.lubricant) || x.supply) && options.length > 0 && (
              <div className="col-span-2 flex w-full gap-2">
                {options.map((o) => (
                  <Big key={o} tone={x.supply === o ? 'go' : 'light'} className="min-h-[56px] flex-1 border-2 border-slate-300 px-2" disabled={!canEdit} onClick={() => setLubes(lubes.map((y, j) => (j === i ? { ...y, supply: y.supply === o ? '' : o } : y)))}>
                    {x.supply === o ? `✓ ${o}` : o}
                  </Big>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {canEdit && <Big tone="light" className="mt-2 border-2 border-slate-300" onClick={() => setLubes([...lubes, { lubricant: OTHER_LUBRICANT, otherName: '', gallons: '', supply: '' }])}>+ Other lubricant</Big>}

      <Label>Remarks</Label>
      <NoteBox value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canEdit} placeholder="Anything the customer should know" />

      {canEdit && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Big disabled={!hoursOk || !gallonsOk} onClick={() => save(false)}>Save</Big>
          <Big tone="go" disabled={!hoursOk || !gallonsOk || !complete} onClick={() => save(true)}>Lube Report Done</Big>
        </div>
      )}
      {needsSupply.length > 0 && canEdit && <p className="mt-2 text-base text-red-800">Choose {options.join(' or ')} for {needsSupply.map((x) => x.lubricant).join(', ')}.</p>}
      {!complete && canEdit && <p className="mt-2 text-base text-slate-600">To finish: hours, type of service, and at least one item marked Check or Change.</p>}
      {(l?.missing.length ?? 0) > 0 && l?.submittedAt === null && <p className="mt-2 text-base text-red-800">Still needed: {l!.missing.join('; ')}</p>}
    </Sheet>
  );
}
